import React, { FC, useEffect, useRef } from 'react';
import { preload } from "react-dom";
import { BalloonEventsOptionsProvider } from "../app/contexts/EmbedOptions";
import { DEFAULT_OPTIONS } from "../app/constants";
import { Layout } from "../app/pages/Layout";
import {
    ApiProvider, BalloonEventsMetadataProvider, ThemeProvider, TranslationsProvider,
    UserProviderChecker, type RouterType
} from "react-ui";
import { BalloonEventsMapOptions } from "../app/types/EmbedOptions";
import translations from "@translations/data/translations.json";
import { Language } from "ts-core";
import { SystemDownWarning } from "../app/components/SystemDownWarning";
import { useMixpanel, registerSuperProperties } from '../app/utils/mixpanel';
import { ZeppelinPositionProvider } from "../app/contexts/ZeppelinPosition";

/**
 * Props for the MapRenderer component.
 * This component renders the full provider hierarchy and is shared between:
 * - App.tsx (instance mode - via BalloonEventsMapInstance)
 * - BalloonEventsMapComponent (inline mode - direct React component)
 */
export interface MapRendererProps {
    /** Map configuration options */
    options: BalloonEventsMapOptions;
    /** Pre-loaded metadata (categories, providers, etc.) */
    metadata: any;
    /** Client embed API object for external access */
    clientEmbedAPI: Record<string, any>;
    /** Function to trigger events back to the embed host */
    clientEmbedEventTrigger: (event: string, data?: any) => void;
    /**
     * Router type to use:
     * - 'browser': BrowserRouter for standalone apps
     * - 'hash': HashRouter for embeds/web components (default for instance mode)
     * - 'none': Use parent's router (for inline React component mode)
     */
    routerType?: RouterType;
}

const fontAwesome = "https://maxcdn.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css";

/**
 * MapRenderer renders the complete map UI with all required providers.
 * 
 * Provider hierarchy:
 * BalloonEventsOptionsProvider → TranslationsProvider → ThemeProvider → 
 * ApiProvider → BalloonEventsMetadataProvider → UserProviderChecker → Layout
 * 
 * This component is used by both rendering paths:
 * 1. Instance mode: App.tsx wraps this with StrictMode and creates root
 * 2. Inline mode: BalloonEventsMapComponent renders this directly in parent tree
 * 
 * @example
 * ```tsx
 * // In App.tsx (instance mode)
 * <MapRenderer
 *   options={options}
 *   metadata={metadata}
 *   clientEmbedAPI={apiObject}
 *   clientEmbedEventTrigger={(e, d) => trigger(e, d)}
 * />
 * 
 * // In BalloonEventsMapComponent (inline mode)
 * <MapRenderer
 *   options={options}
 *   metadata={metadata}
 *   clientEmbedAPI={apiObject}
 *   clientEmbedEventTrigger={(e, d) => eventBus.trigger(e, d)}
 * />
 * ```
 */
export const MapRenderer: FC<MapRendererProps> = ({
    options: rawOptions,
    metadata,
    clientEmbedAPI,
    clientEmbedEventTrigger,
    routerType
}) => {
    useEffect(() => {
        preload(fontAwesome, { as: "style" });
    }, []);

    // Clean undefined values and merge with defaults
    const options = React.useMemo(() => {
        const cleaned = Object.keys(rawOptions).reduce((acc: any, key: string) => {
            const typedKey = key as keyof typeof rawOptions;
            if (rawOptions[typedKey] !== undefined) {
                acc[typedKey] = rawOptions[typedKey];
            }
            return acc;
        }, {} as BalloonEventsMapOptions);
        
        return { ...DEFAULT_OPTIONS, ...cleaned };
    }, [rawOptions]);

    const appRef = useRef<HTMLDivElement>(null);
    
    useMixpanel({
        key: options.key,
        appContainerRef: appRef
    });

    useEffect(() => {
        registerSuperProperties({
            app: 'Map',
            language: options.language ?? '',
            partner_key: options.key,
            is_mobile: window.innerWidth < 768,
        });
    }, [options.language, options.key]);

    return (
        <>
            <link
                rel="stylesheet"
                href={fontAwesome}
            />

            <div ref={appRef} className="w-full h-full relative flex flex-col">
                <BalloonEventsOptionsProvider options={options}>
                    <TranslationsProvider translations={translations} language={options.language as Language}>
                        <div className="w-full h-full relative">
                            <ThemeProvider theme={options.theme!}>
                                <ApiProvider>
                                    <BalloonEventsMetadataProvider legacyAPI={options.legacyAPI ?? false} metadata={metadata}>
                                        <UserProviderChecker partnerKey={options.key}>
                                            <ZeppelinPositionProvider>
                                                <Layout 
                                                    clientEmbedAPI={clientEmbedAPI}
                                                    clientEmbedEventTrigger={clientEmbedEventTrigger}
                                                    routerType={routerType ?? (options.routingType === "hash" ? "hash" : "browser")}
                                                />
                                                <SystemDownWarning />
                                            </ZeppelinPositionProvider>
                                        </UserProviderChecker>
                                    </BalloonEventsMetadataProvider>
                                </ApiProvider>
                            </ThemeProvider>
                        </div>
                    </TranslationsProvider>
                </BalloonEventsOptionsProvider>
            </div>
        </>
    );
};

export default MapRenderer;
